import { Component, OnDestroy, OnInit } from "@angular/core";
import { DateRange } from "@angular/material/datepicker";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { Sort } from "@angular/material/sort";
import { ActivatedRoute, Router } from "@angular/router";
import { IdObject } from "@app/_classes/cosmos-entity";
import { WorkspaceUser } from "@app/_classes/timeghost";
import { debounceTimeAfterFirst } from "@app/_helpers/debounceAfterTime";
import {
	canViewDashboard,
	hasPermissionByKey,
	hasUserRoleControlling,
	hasUserRoleManager,
	isAdmin,
	isSupervisorOfUser,
} from "@app/_helpers/permission";
import { createRxValue, distinctUntilChangedJson, fromRxValue, userIsAwaitingActivation } from "@app/_helpers/utils";
import { AppService } from "@app/app.service";
import { CreditDeductItem } from "@app/components/timecontingent/credit-deduct-dialog/credit-deduct-types";
import { WorkspaceUserDialogComponent } from "@app/components/workspace/update-user-dialog/update-user-dialog.component";
import { ReportsService } from "@app/pages/reports-page/reports.service";
import { MatRightSideSheet } from "@app/shared/mat-right-side-sheet/mat-right-side-sheet";
import { getTimeRangeByKey, TRange } from "@app/shared/time-range-picker/time-range-constants";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Logger, MyTimesService, NotifyService, UserSettingsQuery } from "@timeghost/web-api";
import { addDays, endOfDay, endOfMonth, isSameDay, isValid, startOfDay, startOfMonth } from "date-fns/esm";
import { combineLatest, filter, map, startWith, switchMap } from "rxjs";
import { firstBy } from "thenby";

export type ContingentItem = {
	id: string;
	enabled: boolean;
	future?: boolean;
	employmentDate: Date;
	actualhours: number;
	workinghours: number;
	contingent: number;
	user: WorkspaceUser;
	supervisors?: WorkspaceUser[];
	credit: number;
	credits: CreditDeductItem[];
	items?: any[];
	lastModified: number;
};

export interface WeekContingent {
	days: DayContingent[];
	totalActualHours: number;
	totalBalance: number;
	totalTargetHours: number;
	weekNumber: number;
	year: number;
}
export interface DayContingent {
	actualHours: number;
	balance: number;
	date: string;
	targetHours: number;
}
export interface CurrentContingent {
	actualHours: number;
	balance: number;
	lastModified: number;
	targetHours: number;
	userId: string;
}
export interface MonthContingent {
	credit: number;
	date: string | null;
	totalBalance: number;
}
const log = new Logger("ContingentPageComponent");
@UntilDestroy()
@Component({
	selector: "tg-contingent-page",
	templateUrl: "./contingent-page.component.html",
	styleUrl: "./contingent-page.component.scss",
})
export class ContingentPageComponent implements OnInit, OnDestroy {
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		private myTimesService: MyTimesService,
		private sheet: MatRightSideSheet,
		private notifyService: NotifyService,
		private route: ActivatedRoute,
		private router: Router,
		private dialog: MatDialog,
		private reportsService: ReportsService,
		private appService: AppService
	) {}
	private maxLastModifiedMs = 0;
	private hasSetInitialRange = false;
	readonly maxDate = new Date();
	readonly range = createRxValue<DateRange<Date>>(new DateRange(startOfDay(new Date()), endOfDay(new Date())));
	readonly range$ = this.range.asObservable().pipe(
		map(({ start, end }) => {
			return {
				start,
				end,
			};
		})
	);
	readonly currentUser = this.userSettingsQuery.getValue().id;
	readonly untilDate = this.reportsService.untilDate;
	readonly usersFilter$ = combineLatest([
		this.reportsService.teamFilter.asObservable(),
		this.reportsService.groupFilter.asObservable(),
		this.userSettingsQuery.select(),
	]).pipe(
		map(([users, groups, user]) => {
			const teamIds = this.reportsService.resolveTeamIds(users, groups);
			if (teamIds.length > 0) return teamIds;
			return [user.id];
		})
	);
	openUser = "";
	ngOnInit(): void {
		this.reportsService.lastContingentUrl.update(null);
		this.reportsService.contingentFilterState.update({
			maxCutOffDate: null,
		});
		this.route.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
			const openUser = params["openUser"];
			if (openUser) {
				this.openUser = openUser;
			}
		});
		this.reportsService.untilDate
			.asObservable()
			.pipe(untilDestroyed(this))
			.subscribe((untilDate) => {
				const rangeValues: DateRange<Date> = new DateRange(
					startOfDay(new Date(untilDate)),
					endOfDay(new Date(untilDate))
				);
				this.range.update(rangeValues);
			});
		this.range$.pipe(untilDestroyed(this)).subscribe((range) => {
			const current = new Date(this.reportsService.untilDate.value);
			if (current?.getTime() !== range.start?.getTime()) {
				this.reportsService.untilDate.update(range.start);
			}
		});
		this.entries$
			.asObservable()
			.pipe(untilDestroyed(this))
			.subscribe((entries: ContingentItem[]) => this.onEntriesLoaded(entries));
	}

	ngOnDestroy() {
		this.contingents$.unsubscribe();
		this.contingentMonthly$.unsubscribe();
		this.entries$.unsubscribe();
		this.monthlyEntries$.unsubscribe();
		this.isAdmin$.unsubscribe();
		this.canViewDashboard$.unsubscribe();
		this.contingents$.next([]);
		this.entries$.next([]);
		this.monthlyEntries$.next([]);
	}

	readonly loading = createRxValue(true);
	readonly isAdmin$ = fromRxValue(
		this.userSettingsQuery.select().pipe(
			untilDestroyed(this),
			map((x) => !!x.workspace.users?.find((u) => u.id === x.id && u.admin))
		)
	);
	readonly canViewDashboard$ = fromRxValue(
		this.userSettingsQuery.select().pipe(
			untilDestroyed(this),
			map((x) => {
				const navSettings = canViewDashboard(x);
				if (!navSettings) return null;
				return navSettings;
			})
		)
	);
	private getContingents(userIds?: string[]): Promise<CurrentContingent[]> {
		this.loading.next(true);
		const includeToday = false;
		return this.myTimesService.adapter
			.post<any[]>("/currentContingent", {
				includeToday,
				users: userIds ?? [],
			})
			.toPromise()
			.finally(() => {
				this.loading.next(false);
			});
	}
	private getAllContingent(userIds?: string[], untilDate?: Date): Promise<Record<string, any[]>> {
		return this.myTimesService.adapter
			.post<Record<string, any[]>>("/Get_MonthContigent", {
				users: userIds,
				includeToday: false,
				untilDate:
					startOfDay(new Date(untilDate)) >= startOfDay(new Date())
						? null
						: new Date(untilDate).toISOString(),
			})
			.toPromise()
			.finally(() => {
				this.loading.next(false);
			});
	}
	readonly viewOnlyOwnContingent = this.reportsService.viewOnlyOwnContingent;
	readonly contingents$ = fromRxValue(
		combineLatest([
			this.userSettingsQuery.select().pipe(
				distinctUntilChangedJson(({ workspace }) => [
					workspace.id,
					workspace.users
						?.map(({ id, employment_date, removed, admin }: any) => ({
							id,
							employment_date,
							removed,
							admin,
						}))
						.sort(firstBy((d) => d.id)),
					workspace.permissionSettings,
				]),
				debounceTimeAfterFirst(1000)
			),
			this.reportsService.employeeFilterIncludesRemoved$.asObservable(),
			this.notifyService.onMessage.pipe(
				startWith("init" as const),
				//@ts-ignore
				filter(
					(message) =>
						message === "init" ||
						(message.payload.type === "timecontingent.calculate" && message.payload.state === "completed")
				)
			),
		]).pipe(
			untilDestroyed(this),
			distinctUntilChangedJson(),
			switchMap(async ([user, inlcudeRemoved]) => {
				return await this.getContingents(
					user.workspace.users
						.filter((u) => inlcudeRemoved || !u.removed)
						.map((d) => d.id)
						.filter((d) => !userIsAwaitingActivation(d))
				);
			})
		),
		[]
	);
	readonly contingentMonthly$ = fromRxValue(
		combineLatest([
			this.userSettingsQuery.select().pipe(
				distinctUntilChangedJson(({ workspace }) => [
					workspace.id,
					workspace.users
						?.map(({ id, employment_date, removed, admin }: any) => ({
							id,
							employment_date,
							removed,
							admin,
						}))
						.sort(firstBy((d) => d.id)),
					workspace.permissionSettings,
				]),
				debounceTimeAfterFirst(1000)
			),
			this.reportsService.employeeFilterIncludesRemoved$.asObservable(),
			this.range$.pipe(startWith(null)),
			this.userSettingsQuery.select().pipe(map((x) => x.workspace["timecontingent"].users)),
			this.notifyService.onMessage.pipe(
				startWith("init" as const),
				//@ts-ignore
				filter(
					(message) =>
						message === "init" ||
						(message.payload.type === "timecontingent.calculate" && message.payload.state === "completed")
				)
			),
		]).pipe(
			untilDestroyed(this),
			distinctUntilChangedJson(),
			switchMap(async ([user, inlcudeRemoved, range]) => {
				return await this.getAllContingent(
					user.workspace.users
						.filter((u) => inlcudeRemoved || !u.removed)
						.map((d) => d.id)
						.filter((d) => !userIsAwaitingActivation(d)),
					range.start
				);
			})
		)
	);

	readonly activeSort$ = createRxValue<Sort>(null);
	get activeSort() {
		return this.activeSort$.value;
	}

	changeDataSort(event: Sort) {
		this.reportsService.contingentSort.next(event);
		this.activeSort$.next(event);
	}
	readonly entries$ = fromRxValue(() =>
		combineLatest([
			this.contingents$.asObservable(),
			this.userSettingsQuery.select(),
			this.activeSort$.asObservable(),
			this.usersFilter$,
			this.reportsService.noStartDateToggle.asObservable(),
		]).pipe(
			untilDestroyed(this),
			map(([contingents, { workspace, id: currentUserId }, sort, usersFilter, noStartDate]) => {
				const now = new Date();
				const currentUserAdmin =
					isAdmin({ id: currentUserId, workspace } as any) ||
					hasUserRoleManager({ id: currentUserId, workspace } as any) ||
					hasUserRoleControlling({ id: currentUserId, workspace } as any);
				const missingUsers = workspace.users.filter(
					(d) => !d.removed && !contingents.find((u) => u.userId === d.id)
				);
				let entries = contingents
					.reduce(
						(acc, { actualHours, balance, userId, lastModified }) => {
							const user = workspace.users.find((d) => d.id === userId);
							if (!user) return acc;
							const supervisors = user["supervisors"]
								?.map((s: IdObject) => workspace.users?.find((d) => d.id === s.id))
								.filter(Boolean);

							const credits: (CreditDeductItem & { user: WorkspaceUser })[] = (
								workspace["timecontingent"].users[userId]?.["credit"] ?? []
							).map(({ user, ...d }: CreditDeductItem) => ({
								...d,
								user: workspace.users.find((ud) => ud.id === user)!,
							}));
							const credit =
								credits?.reduce((wcc, w) => {
									if (w.type === "deduct") return (wcc -= w.value);
									return (wcc += w.value);
								}, 0) ?? 0;
							const employmentDate = user["employment_date"] && new Date(user["employment_date"]);
							const enabled = employmentDate && isValid(employmentDate);
							acc.push({
								id: userId,
								enabled,
								employmentDate,
								workinghours: balance,
								actualhours: actualHours,
								contingent: credit + balance,
								user,
								future: enabled && now < employmentDate,
								supervisors,
								credit,
								credits,
								lastModified: lastModified * 1000, // subtract 48 hours in milliseconds remove before commit,
							});
							return acc;
						},
						missingUsers
							.map((user) => {
								if (
									(user.id === currentUserId &&
										!hasPermissionByKey(
											{ id: currentUserId, workspace } as any,
											"groupsCanContingentViewSelf"
										)) ||
									(workspace["timecontingent"]?.["superviseCanViewSubordinates"] &&
									isSupervisorOfUser({ id: currentUserId, workspace } as any, user.id)
										? false
										: !currentUserAdmin)
								)
									return null;
								const employmentDate = user["employment_date"] && new Date(user["employment_date"]);
								const enabled = isValid(employmentDate) && employmentDate.getFullYear() > 2000;
								const future = enabled && employmentDate > now;
								return {
									enabled,
									future,
									id: user.id,
									user,
									actualhours: 0,
									contingent: 0,
									workinghours: 0,
									employmentDate,
									supervisors: user["supervisors"]
										?.map((s: IdObject) => workspace.users?.find((d) => d.id === s.id))
										.filter(Boolean),
									credits: [],
									credit: 0,
									lastModified: 0,
								};
							})
							.filter(Boolean) as ContingentItem[]
					)
					.filter((s) => s && (!usersFilter.length || usersFilter.includes(s.user.id)))
					.filter((s) => noStartDate || s["employmentDate"]);
				log.debug({ entries, timecontingent: workspace["timecontingent"].users });

				if (!sort?.active) sort = { active: "name", direction: "asc" };
				switch (sort.active) {
					case "name":
						entries.sort(firstBy((d) => d.user.name, sort.direction as any));
						break;
					case "workinghours":
						entries.sort(firstBy((d) => d.workinghours, sort.direction as any));
						break;
					case "credit":
						entries.sort(firstBy((d) => d.credits, sort.direction as any));
						break;
					case "contingent":
						entries.sort(firstBy((d) => d.contingent, sort.direction as any));
						break;
					case "lastModified":
						entries.sort(firstBy((d) => d.lastModified, sort.direction as any));
						break;
				}
				return entries.sort(firstBy((item) => (item.id === this.userSettingsQuery.getValue().id ? 0 : 1)));
			})
		)
	);
	readonly isOnMaxLastModifiedDate$ = combineLatest([this.range$, this.entries$.asObservable()]).pipe(
		map(([range, entries]) => {
			const maxMs = this.getMaxLastModified(entries);
			return maxMs > 0 && isSameDay(new Date(range.start), new Date(maxMs));
		})
	);

	readonly monthlyEntries$ = fromRxValue(() =>
		combineLatest([this.contingentMonthly$.asObservable(), this.entries$.asObservable()]).pipe(
			untilDestroyed(this),
			map(([monthlyContingents, entries]) => {
				if (!monthlyContingents) return entries;

				const data = entries.map((entry) => {
					//@ts-ignore
					let months: MonthContingent[] = monthlyContingents.result[entry.id];
					if (!months) {
						return {
							...entry,
							workinghours: 0,
							credit: 0,
							contingent: 0,
						};
					}
					const reversed = [...months].reverse();

					const monthOverview = reversed
						.reduce((acc, month: MonthContingent) => {
							const previousContingent = acc.length > 0 ? acc[acc.length - 1] : 0;

							acc.push({
								...month,
								totalBalanceSummed: month.totalBalance + (previousContingent.totalBalanceSummed ?? 0),
								creditSummed: month.credit + (previousContingent.creditSummed ?? 0),
								gains: month.totalBalance + month.credit,
								contingentSummed:
									month.totalBalance + month.credit + (previousContingent.contingentSummed ?? 0),
							});

							return acc;
						}, [])
						.reverse();

					return {
						...entry,
						workinghours: monthOverview[0].totalBalanceSummed ?? entry.workinghours,
						credit: monthOverview[0].creditSummed ?? entry.credit,
						contingent: monthOverview[0].contingentSummed ?? entry.contingent,
						monthOverview,
					};
				});
				return data.sort(firstBy((item) => (item.id === this.userSettingsQuery.getValue().id ? 0 : 1)));
			})
		)
	);

	private onEntriesLoaded(entries: ContingentItem[]): void {
		this.maxLastModifiedMs = this.getMaxLastModified(entries);
		this.reportsService.contingentFilterState.update({
			maxCutOffDate: this.maxLastModifiedMs > 0 ? startOfDay(new Date(this.maxLastModifiedMs)) : null,
		});
		this.setInitialRangeFromEntries();
	}

	private getMaxLastModified(entries: ContingentItem[]): number {
		const modified = entries.filter((e) => e.lastModified > 0);
		if (!modified.length) return 0;
		return Math.max(...modified.map((e) => e.lastModified));
	}

	private setInitialRangeFromEntries(): void {
		if (this.hasSetInitialRange || this.maxLastModifiedMs <= 0) return;
		this.hasSetInitialRange = true;
		const day = startOfDay(new Date(this.maxLastModifiedMs));
		if (!isSameDay(this.range.value.start, day)) {
			this.range.update(new DateRange(day, endOfDay(day)));
		}
	}

	isToday(timestampMs: number): boolean {
		return isSameDay(new Date(timestampMs), new Date());
	}

	onViewDateChange(next: DateRange<Date>): void {
		const selectedDay = startOfDay(next.start);
		const maxDay = startOfDay(new Date(this.maxLastModifiedMs));
		if (this.maxLastModifiedMs > 0 && selectedDay > maxDay) {
			this.appService.notifyWarn(
				"workspace.timecontingent.dateAfterLastCalculated",
				undefined,
				{
					date: new Date(this.maxLastModifiedMs).toLocaleDateString(),
				},
				undefined,
				4000
			);
		}
		this.range.update(next);
	}

	editUser(id: string) {
		return this.sheet.open(WorkspaceUserDialogComponent, {
			data: {
				userId: id,
				selectedTabIndex: 2,
			},
		});
	}

	async openUsersMonthInDashboard(userId: string, month: string) {
		const userSettings = this.userSettingsQuery.getValue();
		//@ts-ignore
		const employmentDate = new Date(userSettings.workspace.users.find((u) => u.id == userId)["employment_date"]);
		const endOfToday = endOfDay(addDays(new Date(), -1));
		this.reportsService.teamFilter.update([userId]);
		const rType = getTimeRangeByKey("time-range.preset.month");
		const newRange = {
			...(rType || {}),
			from: startOfMonth(new Date(month)) < employmentDate ? employmentDate : startOfMonth(new Date(month)),
			to: endOfMonth(new Date(month)) > endOfToday ? endOfToday : endOfMonth(new Date(month)),
		} as TRange;
		this.reportsService.onRangeUpdate(newRange);
		const rangeParams = this.reportsService.buildRangeQueryParams(newRange);
		this.reportsService.lastContingentUrl.update(this.router.url);
		await this.router.navigate(["/dashboard/schedules"], {
			queryParams: { ...rangeParams, user: userId },
		});
	}
}
